import {
  AreaRecorte,
  garantirPath,
  nomeDeSaida,
  normalizarRecorte,
} from './imageCrop';

export type AppAssetKind = 'icon' | 'splash';
export interface AlvoExport {
  largura: number;
  altura: number;
}
export interface ExportOpts {
  alvo: AlvoExport;
  corDeFundo?: string | null;
  margem?: number;
}
export const ALVO: Record<AppAssetKind, AlvoExport> = {
  icon: {largura: 512, altura: 512},
  splash: {largura: 2732, altura: 2732},
};
export const LIMIAR_UPSCALE = 1.5;
export const MARGEM_MAXIMA = 0.35;
export const CORES_DE_FUNDO = [
  '#ffffff',
  '#f1f5f9',
  '#142b45',
  '#1469d2',
  '#111827',
];
export const normalizarCor = (cor?: string | null): string => {
  const hex = (cor || '').replace(/^#/, '');
  if (/^[\da-f]{6}$/i.test(hex)) return `#${hex.toLowerCase()}`;
  if (/^[\da-f]{3}$/i.test(hex))
    return `#${hex
      .split('')
      .map((letra) => letra + letra)
      .join('')
      .toLowerCase()}`;
  return '#ffffff';
};
export const normalizarMargem = (margem = 0): number =>
  Number.isFinite(margem) ? Math.max(0, Math.min(MARGEM_MAXIMA, margem)) : 0;
export const calcularRetanguloDeDesenho = (
  area: AreaRecorte,
  alvo: AlvoExport,
  margem = 0,
) => {
  if (
    ![area.x, area.y, area.width, area.height, alvo.largura, alvo.altura].every(
      Number.isFinite,
    ) ||
    area.width <= 0 ||
    area.height <= 0 ||
    alvo.largura <= 0 ||
    alvo.altura <= 0
  )
    throw new Error('O recorte tem dimensões inválidas.');
  const escala =
    Math.min(alvo.largura / area.width, alvo.altura / area.height) *
    (1 - 2 * normalizarMargem(margem));
  const dw = area.width * escala;
  const dh = area.height * escala;
  return {
    sx: area.x,
    sy: area.y,
    sw: area.width,
    sh: area.height,
    dx: (alvo.largura - dw) / 2,
    dy: (alvo.altura - dh) / 2,
    dw,
    dh,
  };
};
export const precisaUpscale = (
  largura: number,
  altura: number,
  alvo: AlvoExport,
  margem = 0,
): boolean =>
  largura > 0 &&
  altura > 0 &&
  Math.min(alvo.largura / largura, alvo.altura / altura) *
    (1 - 2 * normalizarMargem(margem)) >
    LIMIAR_UPSCALE;
const centralizar = (w: number, h: number) => ({
  x: (1 - w) / 2,
  y: (1 - h) / 2,
  w,
  h,
});
export const VIEWPORTS_SPLASH = [
  {nome: 'Celular', ...centralizar(9 / 19.5, 1)},
  {nome: 'Tablet', ...centralizar(3 / 4, 1)},
  {nome: 'Paisagem', ...centralizar(1, 9 / 16)},
];
export const framesDeAparelho = (kind: AppAssetKind) =>
  kind === 'splash' ? VIEWPORTS_SPLASH : [];
export const safeAreaCentral = (kind: AppAssetKind) =>
  kind === 'icon' ? centralizar(0.66, 0.66) : centralizar(9 / 19.5, 9 / 16);
export const carregarImagem = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.onload = () =>
      imagem.naturalWidth > 0
        ? resolve(imagem)
        : reject(new Error('A imagem está vazia.'));
    imagem.onerror = () =>
      reject(
        new Error(
          'Não foi possível ler a imagem. Escolha um arquivo PNG, JPG ou WebP válido.',
        ),
      );
    imagem.src = url;
  });
export const desenharRecorte = (
  canvas: HTMLCanvasElement,
  imagem: HTMLImageElement,
  area: AreaRecorte,
  opts: ExportOpts,
): void => {
  const recorte = normalizarRecorte(
    area,
    imagem.naturalWidth,
    imagem.naturalHeight,
  );
  const r = calcularRetanguloDeDesenho(recorte, opts.alvo, opts.margem);
  canvas.width = opts.alvo.largura;
  canvas.height = opts.alvo.altura;
  const contexto = canvas.getContext('2d');
  if (!contexto)
    throw new Error(
      'Seu navegador não conseguiu preparar a imagem. Tente novamente.',
    );
  contexto.imageSmoothingEnabled = true;
  contexto.imageSmoothingQuality = 'high';
  contexto.fillStyle = normalizarCor(opts.corDeFundo);
  contexto.fillRect(0, 0, canvas.width, canvas.height);
  contexto.drawImage(imagem, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy, r.dw, r.dh);
};
export const exportarEmTamanhoAlvo = async (
  arquivo: File,
  area: AreaRecorte,
  opts: ExportOpts,
): Promise<File> => {
  const url = URL.createObjectURL(arquivo);
  try {
    const imagem = await carregarImagem(url);
    const canvas = document.createElement('canvas');
    desenharRecorte(canvas, imagem, area, opts);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((resultado) => {
        if (resultado && resultado.size > 0 && resultado.type === 'image/png')
          resolve(resultado);
        else
          reject(
            new Error('Não foi possível gerar o PNG. Tente uma imagem menor.'),
          );
      }, 'image/png'),
    );
    return garantirPath(
      new File([blob], nomeDeSaida(arquivo.name, 'image/png'), {
        type: 'image/png',
        lastModified: Date.now(),
      }),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
};
